import { History } from 'history';
import { BasicPage } from '@mm/state/interfaces/connected-page';
import { NOT_FOUND } from '@mm/constants/status-codes';
import ChatbotModel from '@mm/state/model/ChatbotModel';
import { OpenPrettyErrorModalParams } from '@mm/state/modules/modalsState/actions';

export const getActiveChatbotId = async (
  currentCompanyBotsList: ChatbotModel[],
  chatbotIdFromPath: number,
  chatbotIdFromUserAttribute: number | undefined,
  getFacebookPageList: () => Promise<BasicPage[] | undefined>,
  retrieveChatbot: (
    chatbotId: number,
    noActiveBotChange?: boolean,
    throwExceptions?: boolean,
  ) => Promise<ChatbotModel | undefined>,
  activateCompany: (companyId: number) => void,
  openPrettyErrorModal: (args: OpenPrettyErrorModalParams) => void,
  history: History,
): Promise<[number, ChatbotModel | undefined]> => {
  // Chatbot ID to open is selected based on priority

  // Priority 1: ID from current URL route
  if (chatbotIdFromPath) {
    // 1A: Check the current company
    const chatbotFromPathExists = currentCompanyBotsList.some(
      bot => bot.id === chatbotIdFromPath,
    );
    if (chatbotFromPathExists) {
      return [chatbotIdFromPath, undefined];
    }

    // 1B: Check if bot belongs to another company
    try {
      const bot = await retrieveChatbot(chatbotIdFromPath, false, true);
      if (bot?.companyId) {
        activateCompany(bot.companyId);
        return [bot.id, bot];
      }
    } catch (e) {
      if (e instanceof Response && e.status === NOT_FOUND) {
        openPrettyErrorModal({
          errorMessage: 'Could not open chatbot from URL',
          title: 'Error',
          onClose: () => {
            history.push('/chatbot-editor');
          },
        });
      }
    }
  }

  // Priority 2: ID from activeChatbotId session user attribute (i.e., the most recently opened bot)
  if (chatbotIdFromUserAttribute) {
    const chatbotFromUserAttributeExists = currentCompanyBotsList.some(
      bot => bot.id === chatbotIdFromUserAttribute,
    );

    if (chatbotFromUserAttributeExists) {
      return [chatbotIdFromUserAttribute, undefined];
    }
  }

  // Priority 3: The first active chatbot that we find from the FB pages in the current company
  const facebookPageList = await getFacebookPageList();
  if (facebookPageList && facebookPageList.length) {
    const chosenFacebookPage = facebookPageList.find(fp => !!fp.activeBotId);
    if (chosenFacebookPage) {
      return [chosenFacebookPage.activeBotId, undefined];
    }
  }

  // Priority 4: Any chat bot, really.
  return [currentCompanyBotsList[0].id, undefined];
};
